import {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/types';

export interface AuthStatus {
  authenticated: boolean;
  hasUser: boolean;
  hasCredentials?: boolean;
  userName?: string;
}

const AUTH_BASE = '/api/auth';

const parseResponse = async (res: Response) => {
  if (!res.ok) {
    try {
      const payload = await res.json();
      throw new Error(payload.error || 'Authentication request failed');
    } catch {
      throw new Error('Authentication request failed');
    }
  }
  return res.json();
};

export const getAuthStatus = async (): Promise<AuthStatus> => {
  const res = await fetch(`${AUTH_BASE}/status`, { credentials: 'include' });
  return parseResponse(res);
};

export const getRegistrationOptions = async (): Promise<PublicKeyCredentialCreationOptionsJSON> => {
  const res = await fetch(`${AUTH_BASE}/registration-options`, {
    credentials: 'include',
  });
  return parseResponse(res);
};

export const verifyRegistration = async (payload: RegistrationResponseJSON): Promise<void> => {
  const res = await fetch(`${AUTH_BASE}/verify-registration`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  await parseResponse(res);
};

export const getAuthenticationOptions =
  async (): Promise<PublicKeyCredentialRequestOptionsJSON> => {
    const res = await fetch(`${AUTH_BASE}/authentication-options`, {
      credentials: 'include',
    });
    return parseResponse(res);
  };

export const verifyAuthentication = async (payload: AuthenticationResponseJSON): Promise<void> => {
  const res = await fetch(`${AUTH_BASE}/verify-authentication`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  await parseResponse(res);
};

export const logout = async (): Promise<void> => {
  const res = await fetch(`${AUTH_BASE}/logout`, {
    method: 'POST',
    credentials: 'include',
  });
  await parseResponse(res);
};

export const resetAuth = async (secret: string): Promise<{ ok: boolean; message: string }> => {
  const res = await fetch(`${AUTH_BASE}/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ secret }),
  });
  return parseResponse(res);
};
